import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { userApi, handleApiError } from '../services/api';

interface UserProfile {
  id: number;
  username: string;
  display_name: string;
  bio?: string;
  followers_count: number;
  following_count: number;
  public_prompts_count: number;
  created_at: string;
}

interface UserCardProps {
  userId: number;
  username: string;
  isVisible: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({ userId, username, isVisible, position, onClose, onMouseEnter, onMouseLeave }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 获取用户资料
  const fetchUserProfile = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await userApi.getProfile(userId);
      
      if (response.success) {
        setProfile(response.data);
      } else {
        setError(response.message || '获取用户资料失败');
      }
    } catch (error) {
      console.error('获取用户资料失败:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long'
    });
  };

  useEffect(() => {
    if (isVisible && userId) {
      fetchUserProfile();
    }
  }, [isVisible, userId, fetchUserProfile]);

  // 计算卡片位置，确保不超出屏幕
  const getCardPosition = () => {
    const cardWidth = 280;
    const cardMaxHeight = 400; // 增加最大高度
    const padding = 16;
    
    let x = position.x;
    let y = position.y;
    
    // 检查右边界
    if (x + cardWidth > window.innerWidth - padding) {
      x = window.innerWidth - cardWidth - padding;
    }
    
    // 检查左边界
    if (x < padding) {
      x = padding;
    }
    
    // 检查下边界
    if (y + cardMaxHeight > window.innerHeight - padding) {
      y = window.innerHeight - cardMaxHeight - padding;
    }
    
    // 检查上边界
    if (y < padding) {
      y = padding;
    }
    
    return { x, y };
  };

  const cardPosition = getCardPosition();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          transition={{ duration: 0.2 }}
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{
            left: cardPosition.x,
            top: cardPosition.y,
            width: '280px',
            maxHeight: '400px'
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="text-red-500 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm">{error}</p>
            </div>
          ) : profile ? (
            <div className="p-6">
              {/* 头像和基本信息 */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-user text-blue-600 dark:text-blue-400"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg truncate">
                    {profile.display_name || profile.username}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">@{profile.username}</p>
                </div>
              </div>

              {/* 个人简介 */}
              <div className="mb-4 max-h-32 overflow-y-auto">
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {profile.bio || '这个家伙很懒，还没有个人简介。'}
                </p>
              </div>

              {/* 加入时间 */}
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>加入于 {formatDate(profile.created_at)}</span>
              </div>
            </div>
          ) : null}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserCard; 